// floating-popover.directive.ts
import {
  Directive,
  ElementRef,
  HostListener,
  Input,
  Renderer2,
  TemplateRef,
  ViewContainerRef,
  AfterViewInit,
  OnDestroy,
  inject
} from '@angular/core';

@Directive({
  selector: '[floatingPopover]',
  exportAs: 'floatingPopover',
  standalone: true
})
export class FloatingPopoverDirective implements AfterViewInit, OnDestroy {
  @Input('floatingPopover') content!: TemplateRef<any>;

  private overlay?: HTMLElement;
  private arrow?: HTMLElement;
  private visible = false;
  private hideTimeout?: any;
  private scrollUnlisten?: () => void;

  private host = inject(ElementRef<HTMLElement>);
  private renderer = inject(Renderer2);
  private vcRef = inject(ViewContainerRef);

  ngAfterViewInit(): void {
    this.buildOverlay();
  }

  ngOnDestroy(): void {
    this.clearHideTimeout();
    this.hide();
  }

  // Show on hover in
  @HostListener('mouseenter')
  onHostMouseEnter(): void {
    this.show();
  }

  // Hide on hover out (with delay)
  @HostListener('mouseleave')
  onHostMouseLeave(): void {
    this.scheduleHide();
  }

  // Show
  private show(): void {
    if (!this.overlay || this.visible) return;
    this.clearHideTimeout();
    this.visible = true;
    this.renderer.appendChild(document.body, this.overlay);
    this.positionOverlay();

    // Hide on **any** scroll (including nested containers)
    this.scrollUnlisten = this.renderer.listen(
      'window',
      'scroll',
      () => { if (this.visible) this.hide(); }
    );
  }

  // Schedule hide with delay
  private scheduleHide(): void {
    this.clearHideTimeout();
    this.hideTimeout = setTimeout(() => this.hide(), 100); // 100ms delay
  }

  // Hide
  private hide(): void {
    if (!this.overlay || !this.visible) return;
    this.clearHideTimeout();
    this.visible = false;
    this.renderer.removeChild(document.body, this.overlay);
    this.scrollUnlisten?.();
    this.scrollUnlisten = undefined;
  }

  // Clear hide timeout
  private clearHideTimeout(): void {
    if (this.hideTimeout) {
      clearTimeout(this.hideTimeout);
      this.hideTimeout = undefined;
    }
  }

  // Build overlay once
  private buildOverlay(): void {
    this.overlay = this.renderer.createElement('div');
    this.renderer.addClass(this.overlay, 'floating-popover');

    // Arrow
    this.arrow = this.renderer.createElement('div');
    this.renderer.addClass(this.arrow, 'floating-arrow');
    this.renderer.appendChild(this.overlay, this.arrow);

    // Content from ng-template
    const view = this.vcRef.createEmbeddedView(this.content);
    view.rootNodes.forEach(n => this.renderer.appendChild(this.overlay, n));

    // Stop inner clicks bubbling (though not needed for hover, kept for consistency)
    this.renderer.listen(this.overlay, 'click', e => e.stopPropagation());

    // Handle mouse enter/leave on overlay to prevent premature hide
    this.renderer.listen(this.overlay, 'mouseenter', () => this.clearHideTimeout());
    this.renderer.listen(this.overlay, 'mouseleave', () => this.scheduleHide());
  }

  // Position with arrow centered on the trigger
  private positionOverlay(): void {
    if (!this.overlay || !this.arrow) return;

    const hostRect = this.host.nativeElement.getBoundingClientRect();
    const ovRect = this.overlay.getBoundingClientRect();
    const gap = 8;

    // Vertical
    let top = hostRect.bottom + gap;
    let arrowTop = -10;
    let arrowCls = '';

    if (top + ovRect.height > window.innerHeight) {
      top = hostRect.top - ovRect.height - gap;
      arrowTop = ovRect.height;
      arrowCls = 'top';
    }

    // Horizontal
    let left = hostRect.left + hostRect.width / 2 - ovRect.width / 2;
    if (left < gap) left = gap;
    if (left + ovRect.width > window.innerWidth - gap) {
      left = window.innerWidth - ovRect.width - gap;
    }

    // Apply
    this.renderer.setStyle(this.overlay, 'top', `${top}px`);
    this.renderer.setStyle(this.overlay, 'left', `${left}px`);

    const arrowLeft = hostRect.left + hostRect.width / 2 - left - 10;
    this.renderer.setStyle(this.arrow, 'left', `${arrowLeft}px`);
    this.renderer.setStyle(this.arrow, 'top', `${arrowTop}px`);
    this.renderer.removeClass(this.arrow, 'top');
    if (arrowCls) this.renderer.addClass(this.arrow, arrowCls);
  }
}
